"use client";

import { useEffect, useState } from "react";
import Loading from "@/app/components/Loading";

const WORLD = "Baunheim";

interface NewsItem {
  id: string;
  at: string;
  kind: string;
  title: string;
  body: string;
  read: boolean;
}

const KIND_LABEL: Record<string, string> = { system: "お知らせ", abort: "進行の中断" };

/** ISO の時刻を 10/1 14:05 の形にする */
function dateLabel(at: string): string {
  const date = new Date(at);
  if (Number.isNaN(date.getTime())) return "";
  return `${date.getMonth() + 1}/${date.getDate()} ${date.getHours()}:${String(date.getMinutes()).padStart(2, "0")}`;
}

/**
 * お知らせの窓。ヘッダの鈴のボタンから開く。システムからのメッセージと進行の中断を、新しい順に件名で並べる。
 * 件名を押すと同じ窓の中で本文を出し、その 1 件を既読にする
 */
export default function PlayNews({ region, slug, onUnread, onClose }: { region: string; slug: string; onUnread: (count: number) => void; onClose: () => void }) {
  const [items, setItems] = useState<NewsItem[] | null>(null);
  const [open, setOpen] = useState<NewsItem | null>(null);
  const [message, setMessage] = useState("");

  useEffect(() => {
    let alive = true;
    fetch(`/api/play/news?world=${WORLD}&region=${encodeURIComponent(region)}&slug=${encodeURIComponent(slug)}`)
      .then(async (response) => {
        const body = await response.json();
        if (!alive) return;
        if (response.ok) setItems((body.items ?? []) as NewsItem[]);
        else setMessage(body.error ?? "お知らせを読めません");
      })
      .catch(() => {
        if (alive) setMessage("サーバーに繋がりません");
      });
    return () => {
      alive = false;
    };
  }, [region, slug]);

  /** 1 件の本文を開く。未読なら既読にする */
  const openItem = (item: NewsItem) => {
    setOpen(item);
    if (item.read) return;
    setItems((now) => (now ? now.map((one) => (one.id === item.id ? { ...one, read: true } : one)) : now));
    fetch("/api/play/news_read", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ world: WORLD, region, slug, id: item.id }),
    })
      .then(async (response) => {
        const body = await response.json();
        if (response.ok && typeof body.news_unread === "number") onUnread(body.news_unread);
      })
      .catch(() => setMessage("既読にできません"));
  };

  return (
    <div className="pl-modal-back pl-modal-back-center" onClick={onClose}>
      <div className="pl-modal pl-modal-center pl-news" role="dialog" aria-label="お知らせ" onClick={(event) => event.stopPropagation()}>
        <div className="pl-modal-head">
          <h3>{open ? open.title : "お知らせ"}</h3>
          <button type="button" className="pl-modal-x" aria-label="閉じる" onClick={onClose}>
            ×
          </button>
        </div>
        <div className="pl-modal-body">
          {open ? (
            <>
              <div className="pl-news-head">
                <span className={open.kind === "abort" ? "pl-news-kind pl-news-kind-abort" : "pl-news-kind"}>{KIND_LABEL[open.kind] ?? "お知らせ"}</span>
                <small>{dateLabel(open.at)}</small>
              </div>
              <p className="pl-news-body">{open.body || "本文はありません。"}</p>
              <button type="button" className="pl-back pl-news-back" onClick={() => setOpen(null)}>
                一覧へ戻る
              </button>
            </>
          ) : items === null ? (
            message ? <p className="pl-note">{message}</p> : <Loading />
          ) : items.length === 0 ? (
            <div className="pl-empty">
              <strong>お知らせはありません。</strong>
              進行が止まったときや、運営からの連絡があるときに、ここに届きます。
            </div>
          ) : (
            <div className="pl-list">
              {items.map((one) => (
                <button key={one.id} type="button" className={one.read ? "pl-list-btn pl-news-row" : "pl-list-btn pl-news-row pl-list-btn-new"} onClick={() => openItem(one)}>
                  <span className="pl-news-line">
                    {one.read ? null : <i className="pl-dot" aria-label="未読" />}
                    <span className="pl-news-title">{one.title}</span>
                  </span>
                </button>
              ))}
            </div>
          )}
          {message && items !== null ? <p className="pl-message">{message}</p> : null}
        </div>
      </div>
    </div>
  );
}
